/**
 * Copyright © 2026 Delicious AI, LLC
 */

import { useQuery } from '@apollo/client'
import {
  Box,
  Chip,
  CircularProgress,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material'
import { useEffect, useState } from 'react'

import {
  GET_DISPLAY_PLANOGRAM_HISTORY,
  GetDisplayPlanogramHistoryQuery,
  GetDisplayPlanogramHistoryQueryVariables,
  PlanogramChangeDetailsFragment,
} from '@/graphql'
import useT from '@/i18n/useT'

import { summaryText, userName } from './planogram-history.helpers'
import PlanogramHistoryDialog from './PlanogramHistoryDialog'

export default function PlanogramHistory({ displayId }: { displayId: string }) {
  const { t } = useT()
  const [selected, setSelected] =
    useState<PlanogramChangeDetailsFragment | null>(null)
  const [page, setPage] = useState(0)
  const [rowsPerPage, setRowsPerPage] = useState(3)
  const { data, loading, error } = useQuery<
    GetDisplayPlanogramHistoryQuery,
    GetDisplayPlanogramHistoryQueryVariables
  >(GET_DISPLAY_PLANOGRAM_HISTORY, {
    variables: {
      displayId,
      pagination: {
        limit: rowsPerPage,
        offset: page * rowsPerPage,
      },
    },
    fetchPolicy: 'cache-and-network',
  })
  const history = data?.displayPlanogramHistory ?? []
  const historyCount = data?.displayPlanogramHistoryCount ?? 0

  useEffect(() => {
    setPage(0)
    setSelected(null)
  }, [displayId])

  return (
    <Box sx={{ mt: 3, width: 'min(88vw, 920px)' }}>
      <Typography variant="h6" sx={{ mb: 1 }}>
        {t('planogramHistory.title', 'Planogram history')}
      </Typography>
      {loading && !data ? (
        <CircularProgress size={24} />
      ) : error ? (
        <Typography color="error">{error.message}</Typography>
      ) : history.length === 0 ? (
        <Typography color="text.secondary">
          {t('planogramHistory.empty', 'No planogram changes yet.')}
        </Typography>
      ) : (
        <Paper variant="outlined">
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>
                    {t('planogramHistory.columns.changed', 'Changed')}
                  </TableCell>
                  <TableCell>
                    {t('planogramHistory.columns.changedBy', 'Changed by')}
                  </TableCell>
                  <TableCell>
                    {t('planogramHistory.columns.summary', 'Summary')}
                  </TableCell>
                  <TableCell>
                    {t(
                      'planogramHistory.columns.storeProgress',
                      'Store progress',
                    )}
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {history.map((change, index) => {
                  const isLatestChange = page * rowsPerPage + index === 0
                  const superseded = !change.completedAt && !isLatestChange
                  const progressLabel = change.completedAt
                    ? t('planogramHistory.status.completed', 'Completed')
                    : superseded
                      ? t('planogramHistory.status.superseded', 'Superseded')
                      : t(
                          'planogramHistory.status.notCompleted',
                          'Not completed',
                        )
                  const progressDescription = change.completedAt
                    ? t(
                        'planogramHistory.status.completedTooltip',
                        'A restock was completed to update the display to this planogram.',
                      )
                    : superseded
                      ? t(
                          'planogramHistory.status.supersededTooltip',
                          'The planogram was updated again before a restock was completed for this version.',
                        )
                      : t(
                          'planogramHistory.status.notCompletedTooltip',
                          'A restock has not yet been completed to update the display to this planogram.',
                        )
                  return (
                    <TableRow
                      hover
                      key={change.id}
                      onClick={() => setSelected(change)}
                      sx={{ cursor: 'pointer' }}
                    >
                      <TableCell>
                        {new Date(change.created).toLocaleString()}
                      </TableCell>
                      <TableCell>
                        {userName(
                          change.changedBy,
                          t('common.unknown', 'Unknown'),
                        )}
                      </TableCell>
                      <TableCell>{summaryText(change, t)}</TableCell>
                      <TableCell>
                        <Tooltip title={progressDescription} arrow>
                          <Chip
                            size="small"
                            color={
                              change.completedAt
                                ? 'success'
                                : superseded
                                  ? 'warning'
                                  : 'default'
                            }
                            label={progressLabel}
                          />
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  )
                })}
              </TableBody>
            </Table>
          </TableContainer>
          <TablePagination
            component="div"
            count={historyCount}
            page={page}
            rowsPerPage={rowsPerPage}
            rowsPerPageOptions={[3, 5, 10, 25]}
            onPageChange={(_, nextPage) => setPage(nextPage)}
            onRowsPerPageChange={event => {
              setRowsPerPage(Number(event.target.value))
              setPage(0)
            }}
          />
        </Paper>
      )}

      <PlanogramHistoryDialog
        change={selected}
        onClose={() => setSelected(null)}
      />
    </Box>
  )
}
